import { useState } from 'react';
import { Link2, Loader2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { WaitingStation } from '@/api/legacy';

interface StationBindingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName: string;
  waitingStations: WaitingStation[];
  onAssign: (stationKey: string, comment: string) => void;
  isAssigning: boolean;
}

function WaitingStationCard({
  station,
  clientName,
  onAssign,
  isAssigning,
}: {
  station: WaitingStation;
  clientName: string;
  onAssign: (stationKey: string, comment: string) => void;
  isAssigning: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [comment, setComment] = useState('');

  const shortKey = station.clientPublicKey.substring(0, 12) + '...';
  const displayName = station.comment?.trim() ? `${station.comment} - ${shortKey}` : shortKey;

  const handleAssign = () => {
    if (confirm(`Привязать станцию ${shortKey} к ${clientName}?`)) {
      onAssign(station.clientPublicKey, comment);
    }
  };

  return (
    <div className="border rounded-lg bg-card">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full p-3 text-left flex items-center justify-between hover:bg-muted/50 rounded-t-lg">
          <span className="text-sm font-medium truncate">{displayName}</span>
          {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3">
          {/* Log */}
          {station.log && (
            <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2 mb-3 max-h-32 overflow-y-auto whitespace-pre-wrap">
              {station.log}
            </div>
          )}

          {/* Assign section */}
          {!showAssign ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowAssign(true)}
            >
              <Link2 className="h-4 w-4 mr-1" />
              Привязать к {clientName}
            </Button>
          ) : (
            <div className="space-y-2">
              <Input
                placeholder="Комментарий (например: Комп бухгалтера)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="text-sm"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setShowAssign(false);
                    setComment('');
                  }}
                >
                  Отмена
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  onClick={handleAssign}
                  disabled={isAssigning}
                >
                  {isAssigning ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-1" />
                  )}
                  Привязать
                </Button>
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function StationBindingSheet({
  open,
  onOpenChange,
  clientName,
  waitingStations,
  onAssign,
  isAssigning,
}: StationBindingSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh]">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Привязка станций
          </SheetTitle>
          <SheetDescription>
            Станции, ожидающие привязки к клиенту
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 overflow-y-auto max-h-[60vh] -mx-4 px-4">
          {/* Empty state */}
          {waitingStations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Станций, ожидающих регистрации, нет
              </p>
            </div>
          )}

          {/* Waiting stations list */}
          {waitingStations.length > 0 && (
            <div className="space-y-2">
              {waitingStations.map((station) => (
                <WaitingStationCard
                  key={station.clientPublicKey}
                  station={station}
                  clientName={clientName}
                  onAssign={onAssign}
                  isAssigning={isAssigning}
                />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
